import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { getRandomLoadingMessage } from '../../utils/loadingMessages';
import { usePWHLPlayers } from '../../hooks/usePWHLPlayers';
import { linkifyPlayers, plainTextToHtml } from '../../utils/linkifyPlayers';
import Navigation from '../common/Navigation';
import Footer from '../common/Footer';

const PWHL_API = 'https://pwhl.unsupervisedbias.com/api/v1';

export default function TrendArticle() {
  const { date, hash } = useParams();
  const [searchParams] = useSearchParams();
  const fromPdub = searchParams.get('ref') === 'pdubfantasy';
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { players } = usePWHLPlayers();

  useEffect(() => {
    window.scrollTo(0, 0);
    const url = `https://unsupervisedbias.com/trends/${date}/${hash}`;
    fetch(`${PWHL_API}/pwhl/articles?page_size=100`)
      .then(r => r.json())
      .then(data => {
        const found = (data.articles || []).find(a => a.url === url);
        if (found) {
          setArticle(found);
          // Unique, descriptive <title> per article. RouteSEO already set the
          // self-referencing canonical for this path; a distinct title helps
          // Google treat each trend article as unique content rather than a
          // duplicate of the SPA shell.
          if (found.title) document.title = `${found.title} | UnsupervisedBias`;
        } else {
          setError('Article not found.');
        }
      })
      .catch(() => setError('Failed to load article.'))
      .finally(() => setLoading(false));
  }, [date, hash]);

  // Parse summary: body text + author footer "— Name, Title · UnsupervisedBias"
  const parseSummary = (summary) => {
    if (!summary) return { body: '', authorName: '', authorTitle: '' };
    const parts = summary.split(/\n\n—\s*/);
    const footer = parts[1] || '';
    const nameMatch = footer.match(/^([^,]+),\s*([^·]+)/);
    return {
      body: parts[0] || '',
      authorName: nameMatch ? nameMatch[1].trim() : footer.split(',')[0].trim(),
      authorTitle: nameMatch ? nameMatch[2].trim() : '',
    };
  };

  const s = {
    page: { background: '#0a0a0a', minHeight: '100vh', color: '#fff' },
    hero: {
      position: 'relative',
      width: '100%',
      maxHeight: '400px',
      overflow: 'hidden',
      background: '#1a1a2e',
    },
    heroImg: { width: '100%', maxHeight: '400px', objectFit: 'cover', opacity: 0.6 },
    container: { maxWidth: '800px', margin: '0 auto', padding: '40px 24px 80px' },
    back: {
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      color: '#d886ff', textDecoration: 'none', fontSize: '0.9rem',
      marginBottom: '32px', opacity: 0.8,
    },
    badge: {
      display: 'inline-block', padding: '4px 14px', borderRadius: '20px',
      background: 'rgba(216, 134, 255, 0.2)', color: '#d886ff',
      fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase',
      marginBottom: '16px',
    },
    title: { fontSize: '2.2rem', fontWeight: '700', lineHeight: 1.25, color: '#fff', marginBottom: '16px' },
    meta: { color: '#888', fontSize: '0.85rem', marginBottom: '32px' },
    divider: { border: 'none', borderTop: '1px solid rgba(255,255,255,0.1)', margin: '32px 0' },
    body: { fontSize: '1.15rem', lineHeight: '1.85', color: '#ccc', whiteSpace: 'pre-wrap' },
    author: { color: '#888', fontSize: '0.9rem', fontStyle: 'italic', marginTop: '32px' },
  };

  return (
    <div style={s.page}>
      <Navigation />

      {loading && (
        <div style={{ textAlign: 'center', padding: '120px 20px', color: '#888' }}>{getRandomLoadingMessage()}</div>
      )}
      {error && (
        <div style={{ textAlign: 'center', padding: '120px 20px', color: '#888' }}>
          <p>{error}</p>
          <Link to="/blog" style={{ color: '#d886ff' }}>← Back to Blog</Link>
      {fromPdub && <a href="https://pdubfantasy.com" style={{ color: '#d886ff', opacity: 0.7, marginLeft: '16px', textDecoration: 'none' }}>← Back to pdubfantasy</a>}
        </div>
      )}
      {article && (() => {
        const { body, authorName, authorTitle } = parseSummary(article.summary);
        const formattedDate = new Date(article.date + 'T12:00:00').toLocaleDateString('en-US', {
          year: 'numeric', month: 'long', day: 'numeric',
        });
        return (
          <>
            {(article.thumbnail || '/ub-trend-thumbnail.jpg') && (
              <div style={s.hero}>
                <img src={article.thumbnail || '/ub-trend-thumbnail.jpg'} alt={article.title} style={s.heroImg} />
              </div>
            )}
            <div style={s.container}>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <Link to="/blog" style={s.back}>← Back to Blog</Link>
                {fromPdub && <a href="https://pdubfantasy.com" style={{ ...s.back, opacity: 0.7 }}>← Back to pdubfantasy</a>}
              </div>
              <div style={s.badge}>PWHL Analysis</div>
              <h1 style={s.title}>{article.title}</h1>

              {/* Author row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                {article.author_avatar && (
                  <img
                    src={article.author_avatar}
                    alt={authorName}
                    style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', objectPosition: 'top center', border: '2px solid #d886ff' }}
                  />
                )}
                <div>
                  <div style={{ color: '#fff', fontWeight: '600', fontSize: '0.95rem' }}>{authorName}</div>
                  {authorTitle && (
                    <div style={{ color: '#888', fontSize: '0.8rem' }}>{authorTitle} · {formattedDate}</div>
                  )}
                </div>
              </div>

              <hr style={s.divider} />
              <div
                style={{ ...s.body, whiteSpace: 'normal' }}
                dangerouslySetInnerHTML={{ __html: linkifyPlayers(plainTextToHtml(body), players) }}
              />
            </div>
          </>
        );
      })()}

      <Footer />
    </div>
  );
}
